// src/riskFlow/ControlEvaluationWizard.js
// Dynamic, config-driven action evaluation wizard (Mantine 8)

import React, { useState, useEffect, useMemo } from 'react';
import { authFetch } from '../utils/authFetch';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card, Text, Button, Stack, Group, Container, Progress,
  TextInput, Select, Badge, Alert, Title, Divider,
  Paper, Grid, Loader, Transition, Radio, MultiSelect, Tooltip
} from '@mantine/core';
import {
  IconShield, IconCalendar, IconEye, IconCheck,
  IconArrowLeft, IconArrowRight, IconAlertCircle, IconClipboardCheck,
  IconCircleCheck, IconCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { updateControlEvaluationField } from '../redux/actions/controlActions';
import { debounce } from 'lodash';
import { Navigation } from '../components/NavigationButtons';

// ----------------- helpers -----------------
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const toSnake = (str) => str.replace(/([A-Z])/g, (m) => `_${m.toLowerCase()}`);
const toLabel = (str) =>
  (str || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (s) => s.toUpperCase());

const buildDefaultEvaluation = (config) => {
  const acc = {};
  (config || []).forEach((section) => {
    (section.properties || []).forEach((p) => {
      const key = toSnake(p.name);
      if (p.fieldType === 'multiselect') acc[key] = Array.isArray(p.defaultValue) ? p.defaultValue : [];
      else if (p.fieldType === 'select' || p.fieldType === 'radio') acc[key] = p.defaultValue ?? null; // IMPORTANT: null, not ''
      else acc[key] = p.defaultValue ?? '';
    });
  });
  return acc;
};

const getRequiredSnakeKeys = (config) => {
  const keys = [];
  (config || []).forEach((section) => {
    (section.properties || []).forEach((p) => {
      if (p.required) keys.push(toSnake(p.name));
    });
  });
  return keys;
};

// Back-compat if config has no `required`
const FALLBACK_REQUIRED = ['control_type', 'self_evaluation', 'control_recurrence', 'active'];

// Map section attribute -> header icon (keeps icon imports in use)
const iconForSection = (attr) => {
  const key = String(attr || '').toLowerCase();
  switch (key) {
    case 'controldetails':
    case 'activation':
      return <IconShield size={18} color="#228be6" />;
    case 'controlperformance':
      return <IconCalendar size={18} color="#868e96" />;
    case 'controlmonitoring':
      return <IconEye size={18} color="#868e96" />;
    case 'auditandreview':
      return <IconClipboardCheck size={18} color="#868e96" />;
    default:
      return null;
  }
};

// ----------------- left sidebar Action card -----------------
const ActionCard = ({ action, index, isSelected, isComplete, onClick }) => {
  return (
    <Paper
      withBorder
      p="lg"
      radius="md"
      onClick={onClick}
      aria-selected={isSelected}
      data-selected={isSelected || undefined}
      sx={(theme) => ({
        position: 'relative',
        cursor: 'pointer',
        borderColor: isSelected ? theme.colors.indigo[5] : theme.colors.gray[3],
        backgroundColor: isSelected ? theme.colors.indigo[1] : theme.white,
        borderWidth: isSelected ? 3 : 1,
        transition: 'all 0.2s ease',
        boxShadow: isSelected 
          ? `0 4px 12px ${theme.colors.indigo[3]}, 0 0 0 3px ${theme.colors.indigo[2]}` 
          : theme.shadows.xs,
        transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
        '&:hover': {
          borderColor: isSelected ? theme.colors.indigo[5] : theme.colors.gray[4],
          boxShadow: isSelected 
            ? `0 6px 16px ${theme.colors.indigo[3]}, 0 0 0 3px ${theme.colors.indigo[2]}` 
            : theme.shadows.sm,
          transform: 'translateY(-1px)',
        },
        '&::before': isSelected
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 6,
              backgroundColor: theme.colors.indigo[6],
              borderTopLeftRadius: theme.radius.md,
              borderBottomLeftRadius: theme.radius.md,
            }
          : {},
      })}
    >
      <Group align="flex-start" wrap="nowrap" gap="sm">
        {isComplete ? <IconCircleCheck size={20} color="#40c057" /> : <IconCircle size={20} color="#868e96" />}
        <Stack gap={4} style={{ flex: 1 }}>
          <Tooltip label={action?.name} multiline withinPortal position="top-start" disabled={!action?.name}>
            <Text size="sm" fw={600} lineClamp={2} style={{ wordBreak: 'break-word' }}>
              {action?.name || `Action ${index + 1}`}
            </Text>
          </Tooltip>
          {action?.description && (
            <Tooltip label={action.description} multiline withinPortal position="top-start">
              <Text size="xs" c="dimmed" lineClamp={2}>
                {action.description}
              </Text>
            </Tooltip>
          )}
          <Badge size="sm" variant="light" color={isComplete ? 'green' : 'gray'}>
            {isComplete ? 'Evaluated' : 'Not Evaluated'}
          </Badge>
        </Stack>
      </Group>
    </Paper>
  );
};

// ===================================================================

export default function ControlEvaluationWizard({
  modelId,
  assessmentUuid,
  riskArea,
  source,
  onNext,
  onBack,
}) {
  const dispatch = useDispatch();

  // ------------ selectors ------------
  const areaRisks = useSelector(
    (s) =>
      s.risks.byAssessment[assessmentUuid]?.modelRisks?.[riskArea]?.areaRisks?.[source.name] || []
  );
  const savedRisk = areaRisks.find((risk) => risk.status === 'Saved');

  const savedControls = useSelector((s) => s.controls.savedControls || {});
  const savedControlEvaluations = useSelector((s) => s.controls.savedControlEvaluations || {});
  const controlEvaluationConfig = useSelector((s) => s.config?.configs?.controlEvaluation || []);

  // controls bound to this risk+model
  const riskControls = savedRisk
    ? (savedControls[savedRisk.riskId] || []).filter((c) => c.modelId === modelId)
    : [];

  // ------------ local state ------------
  const [currentControlIndex, setCurrentControlIndex] = useState(0);
  const [evaluationData, setEvaluationData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showEvaluationPanel, setShowEvaluationPanel] = useState(false);
  const [errors, setErrors] = useState({}); // { snakeKey: 'message' }

  const currentControl = riskControls[currentControlIndex];

  // current evaluation (from Redux, app-loaded)
  const currentControlEvaluationData = useMemo(() => {
    if (currentControl?.controlId && savedRisk?.riskId) {
      return savedControlEvaluations[savedRisk.riskId]?.[currentControl.controlId]?.[0] || {};
    }
    return {};
  }, [currentControl?.controlId, savedRisk?.riskId, savedControlEvaluations]);

  // show panel on initial load
  useEffect(() => {
    if (riskControls.length > 0 && !showEvaluationPanel) setShowEvaluationPanel(true);
  }, [riskControls.length, showEvaluationPanel]);

  // init/merge local form with saved data or config defaults
  useEffect(() => {
    if (!currentControl) return;
    const hasSaved = currentControlEvaluationData && Object.keys(currentControlEvaluationData).length > 0;
    const base = buildDefaultEvaluation(controlEvaluationConfig);
    const newData = hasSaved ? { ...base, ...currentControlEvaluationData } : base;
    setEvaluationData(newData);
    setErrors({}); // reset errors when switching controls
  }, [currentControl, currentControlEvaluationData, controlEvaluationConfig]);

  // debounce autosave
  const saveEvaluationData = useMemo(
    () =>
      debounce(async (data) => {
        if (!currentControl || !savedRisk) return;
        setSaving(true);
        try {
          const response = await authFetch('/.netlify/functions/saveControlEvaluation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              modelId,
              assessmentUuid,
              controlId: currentControl.controlId,
              data,
            }),
          });
          if (!response.ok) throw new Error('Failed to save evaluation');
          notifications.show({
            title: 'Evaluation Saved',
            message: 'Action evaluation has been saved automatically',
            color: 'green',
            icon: <IconCheck size={16} />,
          });
        } catch (error) {
          console.error('Error saving evaluation:', error);
          notifications.show({
            title: 'Save Failed',
            message: 'Failed to save evaluation data',
            color: 'red',
          });
        } finally {
          setSaving(false);
        }
      }, 800),
    [currentControl, savedRisk, modelId, assessmentUuid]
  );

  useEffect(() => {
    return () => {
      if (saveEvaluationData?.cancel) saveEvaluationData.cancel();
    };
  }, [saveEvaluationData]);

  // required keys from config
  const requiredKeys = useMemo(() => getRequiredSnakeKeys(controlEvaluationConfig), [controlEvaluationConfig]);

  // ------------ validation ------------
  const findPropBySnakeKey = (snakeKey) => {
    for (const section of controlEvaluationConfig || []) {
      for (const p of section.properties || []) {
        if (toSnake(p.name) === snakeKey) return p;
      }
    }
    return null;
  };

  const validate = (data) => {
    const keys = requiredKeys.length ? requiredKeys : FALLBACK_REQUIRED.filter((k) => k in data);
    const nextErrors = {};
    keys.forEach((k) => {
      const v = data[k];
      const missing = Array.isArray(v) ? v.length === 0 : !v;
      if (missing) nextErrors[k] = 'This field is required';
      // simple date guard
      const prop = findPropBySnakeKey(k);
      if (prop?.fieldType === 'date' && v) {
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) nextErrors[k] = 'Invalid date';
      }
    });
    return nextErrors;
  };

  const handleFieldChange = (fieldName, value) => {
    const snakeKey = toSnake(fieldName);
    const prop = findPropBySnakeKey(snakeKey);

    // Normalize empty for Select/Radio to null to prevent carry-over
    let normalizedValue = value;
    if ((prop?.fieldType === 'select' || prop?.fieldType === 'radio') && (value === '' || value === undefined)) {
      normalizedValue = null;
    }

    const updatedData = { ...evaluationData, [snakeKey]: normalizedValue };
    setEvaluationData(updatedData);

    // Inline validation for required fields
    setErrors((prev) => {
      const next = { ...prev };
      if (prop?.required) {
        const missing = Array.isArray(normalizedValue) ? normalizedValue.length === 0 : !normalizedValue;
        if (missing) next[snakeKey] = 'This field is required';
        else delete next[snakeKey];
      } else {
        delete next[snakeKey];
      }
      // simple date guard
      if (prop?.fieldType === 'date' && normalizedValue) {
        const d = new Date(normalizedValue);
        if (Number.isNaN(d.getTime())) next[snakeKey] = 'Invalid date';
      }
      return next;
    });

    // Redux update (camelCase fieldName)
    if (currentControl && savedRisk) {
      dispatch(updateControlEvaluationField(savedRisk.riskId, currentControl.controlId, fieldName, normalizedValue));
    }

    // Auto-save
    saveEvaluationData(updatedData);
  };

  // ------------ navigation between actions ------------
  const handlePreviousControl = () => {
    if (currentControlIndex > 0) setCurrentControlIndex((prev) => prev - 1);
  };
  const handleNextControl = () => {
    // hard validation on next
    const v = validate(evaluationData);
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    if (currentControlIndex < riskControls.length - 1) setCurrentControlIndex((prev) => prev + 1);
  };

  // ------------ completion checks ------------
  const isActionComplete = (control) => {
    const evalData = savedControlEvaluations[savedRisk?.riskId]?.[control.controlId]?.[0];
    if (!evalData) return false;
    const keys = requiredKeys.length ? requiredKeys : FALLBACK_REQUIRED.filter((k) => k in evalData);
    return keys.every((k) => {
      const v = evalData[k];
      return Array.isArray(v) ? v.length > 0 : !!v;
    });
  };

  const isCurrentActionComplete = () => {
    const keys = requiredKeys.length ? requiredKeys : FALLBACK_REQUIRED.filter((k) => k in evaluationData);
    return keys.every((k) => {
      const v = evaluationData[k];
      return Array.isArray(v) ? v.length > 0 : !!v;
    });
  };

  const allActionsEvaluated = () => riskControls.every((control) => isActionComplete(control));
  const completedCount = riskControls.filter((c) => isActionComplete(c)).length;

  // ------------ fallbacks ------------
  if (!savedRisk) {
    return (
      <Container fluid px="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="No Saved Risk" color="yellow">
          Please save a risk before evaluating actions.
        </Alert>
      </Container>
    );
  }

  if (riskControls.length === 0) {
    return (
      <Container fluid px="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="No Saved Actions" color="yellow">
          Please save some mitigation actions before evaluating them.
        </Alert>
      </Container>
    );
  }

  // bottom navigation inside the evaluation panel
  const EvalNavButtons = () => (
    <Group justify="space-between" mt="md">
      <Button variant="light" color="gray" leftSection={<IconArrowLeft size={16} />} onClick={handlePreviousControl} disabled={currentControlIndex === 0}>
        Previous
      </Button>
      <Button
        rightSection={<IconArrowRight size={16} />}
        onClick={handleNextControl}
        disabled={!isCurrentActionComplete() || currentControlIndex >= riskControls.length - 1}
      >
        Next
      </Button>
    </Group>
  );

  // ------------------ render ------------------
  return (
    <Container fluid px="xl" py="xl">
      <Stack gap="md">
        {/* Header */}
        <div>
          <Title order={3} c="dark.7">
            Action Evaluation
          </Title>
          <Text size="md" c="dimmed">
            Evaluate the effectiveness of your mitigation actions
          </Text>
        </div>

        <Group gap="md">
          <Text size="md" c="dimmed">
            Risk:
          </Text>
          <Badge
            variant="filled"
            size="lg"
            radius="sm"
            sx={(theme) => ({
              backgroundColor: theme.colors.indigo[5],
              color: theme.white,
            })}
          >
            {savedRisk.refinedRisk || savedRisk.riskName}
          </Badge>
        </Group>

        {/* Progress */}
        <Card withBorder radius="md" p="md">
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                Progress: {completedCount} of {riskControls.length} actions complete
              </Text>
              {saving && <Loader size="sm" />}
            </Group>
            <Progress value={(completedCount / riskControls.length) * 100} size="lg" radius="md" color="indigo" />
          </Stack>
        </Card>

        <Grid gutter="lg">
          {/* Left Column - Actions List */}
          <Grid.Col span={showEvaluationPanel ? 5 : 12}>
            <Card withBorder radius="md" p="lg" h="100%">
              <Stack gap="md">
                <Title order={5} c="dark.7">
                  Mitigation Actions
                </Title>
                <Divider my="xs" />
                <Stack gap="md">
                  {riskControls.map((control, index) => (
                    <ActionCard
                      key={control.controlId}
                      action={control}
                      index={index}
                      isSelected={currentControlIndex === index}
                      isComplete={isActionComplete(control)}
                      onClick={() => setCurrentControlIndex(index)}
                    />
                  ))}
                </Stack>
              </Stack>
            </Card>
          </Grid.Col>

          {/* Right Column - Evaluation Form (slides in) */}
          <Transition mounted={showEvaluationPanel} transition="fade-left" duration={400} timingFunction="ease">
            {(styles) => (
              <Grid.Col span={7} style={styles}>
                <Card withBorder radius="md" p="lg" h="100%">
                  <Stack gap="md">
                    {/* Header (no top prev/next) */}
                    <div>
                      <Group justify="space-between" mb="xs">
                        <Title order={5} c="dark.7">
                          Evaluating: {currentControl?.name || `Action ${currentControlIndex + 1}`} ({currentControlIndex + 1} of {riskControls.length})
                        </Title>
                      </Group>
                      <Text size="sm" c="dimmed" lineClamp={2}>
                        {currentControl?.description}
                      </Text>
                    </div>

                    <Divider />

                    {/* Evaluation Form (dynamic from config) */}
                    <Stack gap="md">
                      {controlEvaluationConfig.map((section) => {
                        const sectionHasRequired = (section.properties || []).some((p) => p.required);
                        return (
                          <Paper withBorder p="md" radius="md" key={section.attribute}>
                            <Stack gap="sm">
                              <Group gap="xs">
                                {iconForSection(section.attribute)}
                                <Text fw={600} size="sm">
                                  {toLabel(section.attribute || 'Section')}
                                </Text>
                                <Badge color={sectionHasRequired ? 'red' : 'gray'} size="sm">
                                  {sectionHasRequired ? 'Required' : 'Optional'}
                                </Badge>
                              </Group>
                              {section.description && (
                                <Text size="xs" c="dimmed">
                                  {section.description}
                                </Text>
                              )}
                              <Grid gutter="sm">
                                {(section.properties || []).map((prop) => {
                                  const snakeKey = toSnake(prop.name);
                                  const componentKey = `${currentControl?.controlId || 'noctrl'}-${snakeKey}`;
                                  let value = evaluationData[snakeKey];
                                  if (value === undefined) {
                                    if (prop.fieldType === 'multiselect') value = [];
                                    else if (prop.fieldType === 'select' || prop.fieldType === 'radio') value = null;
                                    else value = '';
                                  }
                                  const label = toLabel(prop.name);
                                  const common = {
                                    label,
                                    withAsterisk: !!prop.required,
                                    error: errors[snakeKey] || undefined,
                                    size: 'sm',
                                  };

                                  return (
                                    <Grid.Col span={6} key={prop.name}>
                                      {prop.fieldType === 'select' && (
                                        <Select
                                          key={componentKey}
                                          {...common}
                                          placeholder={`Select ${label.toLowerCase()}`}
                                          value={value ?? null}
                                          onChange={(v) => handleFieldChange(prop.name, v)}
                                          data={Array.isArray(prop.options) ? prop.options : []}
                                        />
                                      )}

                                      {prop.fieldType === 'multiselect' && (
                                        <MultiSelect
                                          key={componentKey}
                                          {...common}
                                          placeholder={`Select ${label.toLowerCase()}`}
                                          value={Array.isArray(value) ? value : []}
                                          onChange={(v) => handleFieldChange(prop.name, v)}
                                          data={Array.isArray(prop.options) ? prop.options : []}
                                        />
                                      )}

                                      {prop.fieldType === 'radio' && Array.isArray(prop.options) && (
                                        <Radio.Group
                                          key={componentKey}
                                          {...common}
                                          value={value ?? ''}
                                          onChange={(v) => handleFieldChange(prop.name, v)}
                                        >
                                          <Group mt="xs">
                                            {prop.options.map((opt) => (
                                              <Radio key={opt} value={opt} label={opt} />
                                            ))}
                                          </Group>
                                        </Radio.Group>
                                      )}

                                      {prop.fieldType === 'date' && (
                                        <TextInput
                                          key={componentKey}
                                          type="date"
                                          {...common}
                                          value={formatDateForInput(value)}
                                          onChange={(e) => handleFieldChange(prop.name, e.currentTarget.value)}
                                        />
                                      )}

                                      {prop.fieldType === 'file' && (
                                        <TextInput
                                          key={componentKey}
                                          type="file"
                                          {...common}
                                          onChange={(e) => handleFieldChange(prop.name, e.currentTarget.value)}
                                        />
                                      )}

                                      {(prop.fieldType === 'text' || !prop.fieldType) && (
                                        <TextInput
                                          key={componentKey}
                                          {...common}
                                          value={typeof value === 'string' ? value : ''}
                                          onChange={(e) => handleFieldChange(prop.name, e.currentTarget.value)}
                                        />
                                      )}
                                      {prop.description && (
                                        <Text size="xs" c="dimmed" mt={4}>
                                          {prop.description}
                                        </Text>
                                      )}
                                    </Grid.Col>
                                  );
                                })}
                              </Grid>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>

                    {/* Bottom Navigation (only here) */}
                    <Divider mt="md" />
                    <EvalNavButtons />
                  </Stack>
                </Card>
              </Grid.Col>
            )}
          </Transition>
        </Grid>

        {/* Main Navigation (kept as before) */}
        <Navigation
          onBack={onBack}
          onNext={onNext}
          nextLabel="Continue to Residual Risk"
          nextDisabled={!allActionsEvaluated()}
          showBack={!!onBack}
        />
      </Stack>
    </Container>
  );
}
